import { Table2 } from 'lucide-react';
import { useState, type ReactNode } from 'react';

import { cn } from '@/lib/utils';

/*
 * Gráficas sencillas en SVG (sin librería): columnas (una o dos series apiladas) y barras
 * horizontales. Marcas delgadas con punta redondeada, rejilla tenue, tooltip al pasar o enfocar
 * cada marca, leyenda cuando hay dos series y una vista de tabla (los valores nunca dependen solo
 * del color ni del tooltip). Colores: `--serie-1` / `--serie-2` por tema.
 */

const fmt = new Intl.NumberFormat('es-MX');

export interface Serie {
  clave: string;
  nombre: string;
  color: string;
}

export interface Punto {
  etiqueta: string;
  valores: Record<string, number>;
}

/** Tope "limpio" para el eje: 1, 2 o 5 × potencia de 10. */
function tope(max: number): number {
  if (max <= 0) return 1;
  const p = 10 ** Math.floor(Math.log10(max));
  return [1, 2, 5, 10].map((m) => m * p).find((v) => v >= max) ?? max;
}

function Tarjeta({
  titulo,
  subtitulo,
  series,
  tabla,
  children,
}: {
  titulo: string;
  subtitulo?: string;
  series?: Serie[];
  tabla: ReactNode;
  children: ReactNode;
}) {
  const [verTabla, setVerTabla] = useState(false);
  return (
    <section className="panel-vidrio space-y-3">
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h2 className="font-extrabold">{titulo}</h2>
          {subtitulo && <p className="text-xs text-texto-3">{subtitulo}</p>}
        </div>
        <button
          type="button"
          className="btn-cs btn-vidrio px-2 py-1 text-xs"
          aria-pressed={verTabla}
          onClick={() => setVerTabla((v) => !v)}
        >
          <Table2 size={14} aria-hidden /> {verTabla ? 'Ver gráfica' : 'Ver tabla'}
        </button>
      </header>
      {series && series.length > 1 && !verTabla && (
        <ul className="flex flex-wrap gap-4 text-xs text-texto-2" aria-label="Leyenda">
          {series.map((s) => (
            <li key={s.clave} className="flex items-center gap-1.5">
              <span
                className="h-2.5 w-2.5 rounded-sm"
                style={{ background: s.color }}
                aria-hidden
              />
              {s.nombre}
            </li>
          ))}
        </ul>
      )}
      {verTabla ? <div className="max-h-80 overflow-auto">{tabla}</div> : children}
    </section>
  );
}

function Tooltip({ x, y, children }: { x: number; y: number; children: ReactNode }) {
  return (
    <div
      role="status"
      className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-lg border border-[var(--separador)] bg-[var(--fondo-base)] px-2.5 py-1.5 text-xs whitespace-nowrap shadow-lg"
      style={{ left: x, top: y - 8 }}
    >
      {children}
    </div>
  );
}

/** Columnas por categoría (meses); con dos series se apilan con un hueco de 2px. */
export function Columnas({
  titulo,
  subtitulo,
  series,
  datos,
  formato = (v) => fmt.format(v),
}: {
  titulo: string;
  subtitulo?: string;
  series: Serie[];
  datos: Punto[];
  formato?: (v: number) => string;
}) {
  const [activo, setActivo] = useState<number | null>(null);
  const alto = 200;
  const arriba = 12;
  const izq = 44;
  const ancho = Math.max(320, datos.length * 40 + izq);
  const totales = datos.map((d) => series.reduce((s, x) => s + (d.valores[x.clave] ?? 0), 0));
  const max = tope(Math.max(0, ...totales));
  const banda = (ancho - izq) / Math.max(1, datos.length);
  const grosor = Math.min(24, banda * 0.6);
  const y = (v: number) => arriba + alto - (v / max) * alto;
  const ticks = [0, max / 2, max];

  const tabla = (
    <table className="tabla-cs w-full text-sm">
      <thead>
        <tr>
          <th>Mes</th>
          {series.map((s) => (
            <th key={s.clave} className="text-right">
              {s.nombre}
            </th>
          ))}
          {series.length > 1 && <th className="text-right">Total</th>}
        </tr>
      </thead>
      <tbody>
        {datos.map((d, i) => (
          <tr key={d.etiqueta}>
            <td>{d.etiqueta}</td>
            {series.map((s) => (
              <td key={s.clave} className="tabular text-right">
                {formato(d.valores[s.clave] ?? 0)}
              </td>
            ))}
            {series.length > 1 && (
              <td className="tabular text-right">{formato(totales[i] ?? 0)}</td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );

  return (
    <Tarjeta titulo={titulo} subtitulo={subtitulo} series={series} tabla={tabla}>
      <div className="relative overflow-x-auto">
        <svg
          width={ancho}
          height={arriba + alto + 28}
          viewBox={`0 0 ${ancho} ${arriba + alto + 28}`}
          role="img"
          aria-label={titulo}
          className="overflow-visible"
          onPointerLeave={() => setActivo(null)}
        >
          {ticks.map((t) => (
            <g key={t}>
              <line
                x1={izq}
                x2={ancho}
                y1={y(t)}
                y2={y(t)}
                stroke="var(--rejilla)"
                strokeWidth={1}
              />
              <text
                x={izq - 6}
                y={y(t) + 4}
                textAnchor="end"
                className="fill-[var(--color-texto-3)] text-[10px]"
              >
                {formato(t)}
              </text>
            </g>
          ))}
          {datos.map((d, i) => {
            const cx = izq + banda * i + banda / 2;
            let base = arriba + alto;
            return (
              <g
                key={d.etiqueta}
                tabIndex={0}
                aria-label={`${d.etiqueta}: ${series.map((s) => `${s.nombre} ${formato(d.valores[s.clave] ?? 0)}`).join(', ')}`}
                onPointerEnter={() => setActivo(i)}
                onFocus={() => setActivo(i)}
                onBlur={() => setActivo(null)}
                className="outline-none"
              >
                {/* zona de toque más grande que la marca */}
                <rect
                  x={cx - banda / 2}
                  y={0}
                  width={banda}
                  height={arriba + alto}
                  fill="transparent"
                />
                {series.map((s, k) => {
                  const v = d.valores[s.clave] ?? 0;
                  if (v <= 0) return null;
                  const h = (v / max) * alto;
                  const ultimo = series.slice(k + 1).every((x) => (d.valores[x.clave] ?? 0) <= 0);
                  const top = base - h;
                  const r = ultimo ? Math.min(4, h / 2, grosor / 2) : 0;
                  const path = `M${cx - grosor / 2},${base} V${top + r} Q${cx - grosor / 2},${top} ${cx - grosor / 2 + r},${top} H${cx + grosor / 2 - r} Q${cx + grosor / 2},${top} ${cx + grosor / 2},${top + r} V${base} Z`;
                  base = top - (ultimo ? 0 : 2);
                  return (
                    <path
                      key={s.clave}
                      d={path}
                      fill={s.color}
                      opacity={activo === null || activo === i ? 1 : 0.55}
                    />
                  );
                })}
                <text
                  x={cx}
                  y={arriba + alto + 16}
                  textAnchor="middle"
                  className="fill-[var(--color-texto-3)] text-[10px]"
                >
                  {d.etiqueta}
                </text>
              </g>
            );
          })}
        </svg>
        {activo !== null && datos[activo] && (
          <Tooltip x={izq + banda * activo + banda / 2} y={y(totales[activo] ?? 0)}>
            <p className="mb-0.5 text-texto-3">{datos[activo].etiqueta}</p>
            {series.map((s) => (
              <p key={s.clave} className="flex items-center gap-1.5">
                <span className="h-0.5 w-3" style={{ background: s.color }} aria-hidden />
                <strong>{formato(datos[activo]?.valores[s.clave] ?? 0)}</strong>
                <span className="text-texto-2">{s.nombre}</span>
              </p>
            ))}
          </Tooltip>
        )}
      </div>
    </Tarjeta>
  );
}

/** Barras horizontales de una serie (rankings: licencias, especialidades). */
export function Barras({
  titulo,
  subtitulo,
  datos,
  color = 'var(--serie-1)',
  etiquetaValor = 'Valor',
}: {
  titulo: string;
  subtitulo?: string;
  datos: { etiqueta: string; valor: number }[];
  color?: string;
  etiquetaValor?: string;
}) {
  const max = Math.max(1, ...datos.map((d) => d.valor));
  const tabla = (
    <table className="tabla-cs w-full text-sm">
      <thead>
        <tr>
          <th>Nombre</th>
          <th className="text-right">{etiquetaValor}</th>
        </tr>
      </thead>
      <tbody>
        {datos.map((d) => (
          <tr key={d.etiqueta}>
            <td>{d.etiqueta}</td>
            <td className="tabular text-right">{fmt.format(d.valor)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
  return (
    <Tarjeta titulo={titulo} subtitulo={subtitulo} tabla={tabla}>
      {datos.length === 0 ? (
        <p className="py-6 text-center text-sm text-texto-3">Sin datos</p>
      ) : (
        <ul className="max-h-80 space-y-1.5 overflow-y-auto pr-1">
          {datos.map((d) => (
            <li
              key={d.etiqueta}
              className="grid grid-cols-[minmax(8rem,14rem)_1fr_auto] items-center gap-2 text-xs"
              title={`${d.etiqueta}: ${fmt.format(d.valor)}`}
            >
              <span className="truncate text-texto-2">{d.etiqueta}</span>
              <span className="h-3">
                <span
                  className={cn('block h-3 rounded-r-[4px]')}
                  style={{ width: `${Math.max(1, (d.valor / max) * 100)}%`, background: color }}
                />
              </span>
              <span className="tabular w-12 text-right font-semibold">{fmt.format(d.valor)}</span>
            </li>
          ))}
        </ul>
      )}
    </Tarjeta>
  );
}

/** KPI: etiqueta, valor y comparación opcional. */
export function Kpi({
  etiqueta,
  valor,
  detalle,
  enlace,
}: {
  etiqueta: string;
  valor: string;
  detalle?: string;
  enlace?: ReactNode;
}) {
  return (
    <div className="rounded-xl border border-[var(--separador)] p-3">
      <p className="text-xs text-texto-3">{etiqueta}</p>
      <p className="text-2xl font-extrabold tabular">{valor}</p>
      {detalle && <p className="text-xs text-texto-2">{detalle}</p>}
      {enlace}
    </div>
  );
}
